// This file is auto-generated by @hey-api/openapi-ts

import type { CancelablePromise } from './core/CancelablePromise';
import { OpenAPI } from './core/OpenAPI';
import { request as __request } from './core/request';
import type { GetApiListingsData, GetApiListingsResponse, DeleteApiListingsByIdData, DeleteApiListingsByIdResponse, PatchApiListingsByIdData, PatchApiListingsByIdResponse, GetApiListingsDraftsResponse, PostApiListingsDraftsData, PostApiListingsDraftsResponse, PostApiListingsPublishData, PostApiListingsPublishResponse, GetApiListingData, GetApiListingResponse, GetApiListingsByIdRelatedData, GetApiListingsByIdRelatedResponse, GetApiListingsMyListingsResponse } from './types.gen';

/**
 * Retrieve a paginated list of listings
 * Returns a paginated array of real estate listings. Uses a snapshot timestamp to ensure consistency across pages.
 * @param data The data for the request.
 * @param data.postcodes
 * @param data.localities List of locality ids.
 * @param data.latitude Latitude of the center point for distance-based search
 * @param data.longitude Longitude of the center point for distance-based search
 * @param data.distance Maximum distance from the provided coordinates (in kilometers)
 * @param data.page Page number (1-based indexing)
 * @param data.limit Number of items per page
 * @param data.snapshot Timestamp of when the search was initiated (ISO 8601 format). Used to maintain consistency across pages.
 * @returns unknown Successful retrieval of listings
 * @throws ApiError
 */
export const getApiListings = (data: GetApiListingsData = {}): CancelablePromise<GetApiListingsResponse> => {
    return __request(OpenAPI, {
        method: 'GET',
        url: '/api/listings',
        query: {
            postcodes: data.postcodes,
            localities: data.localities,
            latitude: data.latitude,
            longitude: data.longitude,
            distance: data.distance,
            page: data.page,
            limit: data.limit,
            snapshot: data.snapshot
        }
    });
};

/**
 * Delete a listing by ID
 * Deletes a listing owned by the authenticated user
 * @param data The data for the request.
 * @param data.id The ID of the listing to delete
 * @returns void Listing successfully deleted
 * @throws ApiError
 */
export const deleteApiListingsById = (data: DeleteApiListingsByIdData): CancelablePromise<DeleteApiListingsByIdResponse> => {
    return __request(OpenAPI, {
        method: 'DELETE',
        url: '/api/listings/{id}',
        path: {
            id: data.id
        },
        errors: {
            401: 'Unauthorized access',
            403: 'Forbidden - User does not own the listing',
            404: 'Listing not found',
            500: 'Internal server error'
        }
    });
};

/**
 * Update an existing listing
 * Partially updates a listing owned by the authenticated user
 * @param data The data for the request.
 * @param data.id The ID of the listing to update
 * @param data.requestBody
 * @returns Listing Listing successfully updated
 * @throws ApiError
 */
export const patchApiListingsById = (data: PatchApiListingsByIdData): CancelablePromise<PatchApiListingsByIdResponse> => {
    return __request(OpenAPI, {
        method: 'PATCH',
        url: '/api/listings/{id}',
        path: {
            id: data.id
        },
        body: data.requestBody,
        mediaType: 'application/json',
        errors: {
            400: 'Invalid input',
            401: 'Unauthorized access',
            403: 'Forbidden - User does not own the listing',
            404: 'Listing not found',
            500: 'Internal server error'
        }
    });
};

/**
 * Retrieves the list of drafts for the authorised user
 * @returns ListingDraft Successful retrieval of listing drafts
 * @throws ApiError
 */
export const getApiListingsDrafts = (): CancelablePromise<GetApiListingsDraftsResponse> => {
    return __request(OpenAPI, {
        method: 'GET',
        url: '/api/listings/drafts'
    });
};

/**
 * Post a draft listing
 * @param data The data for the request.
 * @param data.requestBody
 * @returns ListingResponse Draft updated successfully
 * @returns ListingResponse Draft created successfully
 * @throws ApiError
 */
export const postApiListingsDrafts = (data: PostApiListingsDraftsData): CancelablePromise<PostApiListingsDraftsResponse> => {
    return __request(OpenAPI, {
        method: 'POST',
        url: '/api/listings/drafts',
        body: data.requestBody,
        mediaType: 'application/json',
        errors: {
            400: 'Invalid input or missing required fields',
            401: 'Unauthorized access',
            500: 'Internal server error'
        }
    });
};

/**
 * Publish a new listing
 * Publishes a new listing.
 * @param data The data for the request.
 * @param data.requestBody
 * @returns ListingResponse Listing published successfully
 * @throws ApiError
 */
export const postApiListingsPublish = (data: PostApiListingsPublishData): CancelablePromise<PostApiListingsPublishResponse> => {
    return __request(OpenAPI, {
        method: 'POST',
        url: '/api/listings/publish',
        body: data.requestBody,
        mediaType: 'application/json',
        errors: {
            400: 'Invalid input or draft is not ready for publishing',
            401: 'Unauthorized access',
            404: 'Draft listing not found',
            500: 'Internal server error'
        }
    });
};

/**
 * @param data The data for the request.
 * @param data.id The ID of the listing to retrieve.
 * @returns Listing Successful retrieval of the listing.
 * @throws ApiError
 */
export const getApiListing = (data: GetApiListingData): CancelablePromise<GetApiListingResponse> => {
    return __request(OpenAPI, {
        method: 'GET',
        url: '/api/listing',
        query: {
            id: data.id
        },
        errors: {
            404: 'Listing not found.'
        }
    });
};

/**
 * Get listings related to the specified listing
 * Returns a list of related listings based on location proximity and same suburb
 * @param data The data for the request.
 * @param data.id The ID of the listing to find related listings for
 * @returns Listing Successful retrieval of related listings
 * @throws ApiError
 */
export const getApiListingsByIdRelated = (data: GetApiListingsByIdRelatedData): CancelablePromise<GetApiListingsByIdRelatedResponse> => {
    return __request(OpenAPI, {
        method: 'GET',
        url: '/api/listings/{id}/related',
        path: {
            id: data.id
        },
        errors: {
            404: 'Original listing not found',
            500: 'Internal server error'
        }
    });
};

/**
 * Retrieve listings owned by the authenticated user
 * Returns an array of listings owned by the authenticated user
 * @returns Listing Successful retrieval of user's listings
 * @throws ApiError
 */
export const getApiListingsMyListings = (): CancelablePromise<GetApiListingsMyListingsResponse> => {
    return __request(OpenAPI, {
        method: 'GET',
        url: '/api/listings/my-listings',
        errors: {
            401: 'Unauthorized access',
            500: 'Internal server error'
        }
    });
};